import { debounceAsync } from "./debounce";
export class EnquiryService {
    constructor(options) {
        this.options = options;
        this.countCallbackDebounced = debounceAsync((signal, enquiryFilter) => {
            return this.options.countCallback(enquiryFilter, signal);
        });
        this.accosCallbackDebounced = debounceAsync((signal, enquiryFilter) => {
            return this.options.accosCallback(enquiryFilter, signal);
        });
        this.saveCallbackDebounced = debounceAsync((signal, enquiryData) => {
            return this.options.saveCallback(enquiryData, signal);
        });
    }
    async getCount(filter) {
        const enquiryFilter = this.getEnquiryFilter(filter);
        return await this.countCallbackDebounced(enquiryFilter);
    }
    async getAccos(filter) {
        const enquiryFilter = this.getEnquiryFilter(filter);
        Object.assign(enquiryFilter, { excludeCompanyIds: [] });
        return await this.accosCallbackDebounced(enquiryFilter);
    }
    async saveEnquiry(travelData, filter, personalData) {
        const enquiryData = this.getEnquiryData(travelData, filter, personalData);
        const result = await this.saveCallbackDebounced(enquiryData);
        if (result.success == true) {
            this.options.trackStep("thankyou");
            this.options.trackSuccess(enquiryData, result);
            const successPage = await this.options.successCallback({ enquiryData, result }).catch((error) => {
                console.error(error);
                return null;
            });
            return {
                success: true,
                successPage
            };
        }
        else if (result.validationErrors) {
            return {
                success: false,
                // We try to beautify validation error field names:
                validationErrors: Object.entries(result.validationErrors).map(([key, msg]) => ({ key: capitalCase(key), msg }))
            };
        }
        else {
            throw result;
        }
        function capitalCase(text) {
            if (typeof text === "string") {
                return text.toLowerCase().replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()));
            }
            return text;
        }
    }
    getFormValues(value) {
        var _a, _b, _c;
        const filter = value.filter;
        const accotypeIds = ((_a = filter === null || filter === void 0 ? void 0 : filter.accotypes) === null || _a === void 0 ? void 0 : _a.map((_) => `${_.id}|${_.ratingFrom}-${_.ratingTo}`)) || [];
        return {
            travelData: {
                range: value.arrival ? [value.arrival, value.departure] : undefined,
                rooms: value.rooms,
                hasPets: value.hasPets,
                petsMessage: value.petsMessage,
                tag: value.tag,
            },
            filterData: {
                accotypes: {
                    accotypeIds: (_c = (_b = this.options.filter) === null || _b === void 0 ? void 0 : _b.accotypes) === null || _c === void 0 ? void 0 : _c.filter((_) => {
                        const ids = _.id.split(",");
                        return ids.filter((id) => accotypeIds.includes(id)).length === ids.length;
                    }).map((_) => _.id),
                    boardingIds: filter === null || filter === void 0 ? void 0 : filter.boardingIds,
                },
                areas: {
                    regionIds: filter === null || filter === void 0 ? void 0 : filter.regionIds,
                    resortIds: filter === null || filter === void 0 ? void 0 : filter.areaIds,
                },
                themesAmenities: {
                    amenetyIds: filter === null || filter === void 0 ? void 0 : filter.amenityIds,
                    themeIds: filter === null || filter === void 0 ? void 0 : filter.themeIds,
                },
            },
            personalData: {
                email: value.email,
                title: value.title,
                firstname: value.firstname,
                lastname: value.lastname,
                message: value.message,
                phone: value.phone,
                newsletter: value.newsletter,
                privacy: value.privacy,
            },
        };
    }
    getEnquiryData(travelData, filter, personalData) {
        var _a, _b, _c;
        const options = this.options;
        const enquiryData = {
            portalId: options.portalId,
            language: options.language,
            enquiryType: options.enquiryType,
            referer: options.referer,
            track: JSON.stringify(options.track),
            ip: options.ip,
            arrival: (_a = travelData.range) === null || _a === void 0 ? void 0 : _a[0],
            departure: (_b = travelData.range) === null || _b === void 0 ? void 0 : _b[1],
            rooms: (_c = travelData.rooms) === null || _c === void 0 ? void 0 : _c.map((_) => ({
                adults: _.adults || 0,
                children: _.children || 0,
                childrenAges: _.childrenAges || [],
            })),
            hasPets: travelData.hasPets,
            petsMessage: travelData.petsMessage,
            title: personalData.title,
            firstname: personalData.firstname,
            lastname: personalData.lastname,
            email: personalData.email,
            phone: personalData.phone,
            message: personalData.message,
            privacy: personalData.privacy,
            newsletter: personalData.newsletter,
            companyIds: options.enquiryType == 2 ? [options.companyId] : undefined,
            wishedOffers: filter.wishedOffers || 40,
            filter: this.getEnquiryFilter(filter),
            tag: travelData.tag,
        };
        return enquiryData;
    }
    getEnquiryFilter(filter) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        const splitIds = (ids) => `${ids}`.split(",").map((_) => +_);
        let areaIds = ((_b = (_a = filter === null || filter === void 0 ? void 0 : filter.areas) === null || _a === void 0 ? void 0 : _a.resortIds) === null || _b === void 0 ? void 0 : _b.flatMap(splitIds)) || [];
        const regionIds = ((_d = (_c = filter === null || filter === void 0 ? void 0 : filter.areas) === null || _c === void 0 ? void 0 : _c.regionIds) === null || _d === void 0 ? void 0 : _d.flatMap(splitIds)) || [];
        // if all areas of a region are selected
        // then we remove all areaIds of that region
        // and just remember the selected regionId
        const areasIdsToRemove = [];
        const filterOptions = this.options.filter || {};
        for (const regionId of ((_e = filterOptions.regions) === null || _e === void 0 ? void 0 : _e.flatMap((_) => splitIds(_.id))) || []) {
            if (regionIds.includes(regionId)) {
                const children = ((_f = filterOptions.resorts) === null || _f === void 0 ? void 0 : _f.filter((_) => _.parentIds && _.parentIds.includes(regionId))) || [];
                const regionAreaIds = children.flatMap((_) => splitIds(_.id));
                var intersects = regionAreaIds.every((id) => areaIds.includes(id));
                if (intersects) {
                    areasIdsToRemove.push(...regionAreaIds);
                }
            }
        }
        areaIds = areaIds.filter((_) => !areasIdsToRemove.includes(_));
        return {
            areaIds: areaIds,
            regionIds: regionIds,
            themeIds: ((_h = (_g = filter === null || filter === void 0 ? void 0 : filter.themesAmenities) === null || _g === void 0 ? void 0 : _g.themeIds) === null || _h === void 0 ? void 0 : _h.flatMap(splitIds)) || [],
            accotypes: ((_k = (_j = filter === null || filter === void 0 ? void 0 : filter.accotypes) === null || _j === void 0 ? void 0 : _j.accotypeIds) === null || _k === void 0 ? void 0 : _k.flatMap((ids) => `${ids}`.split(",")).map((_) => {
                const [id, rating] = _.split("|");
                const [from, to] = rating.split("-");
                return {
                    id: +id,
                    ratingFrom: +from,
                    ratingTo: +to,
                };
            })) || [],
            amenityIds: ((_m = (_l = filter === null || filter === void 0 ? void 0 : filter.themesAmenities) === null || _l === void 0 ? void 0 : _l.amenetyIds) === null || _m === void 0 ? void 0 : _m.flatMap(splitIds)) || [],
            boardingIds: ((_p = (_o = filter === null || filter === void 0 ? void 0 : filter.accotypes) === null || _o === void 0 ? void 0 : _o.boardingIds) === null || _p === void 0 ? void 0 : _p.flatMap(splitIds)) || [],
            excludeCompanyIds: ((_q = filter === null || filter === void 0 ? void 0 : filter.excludeCompanyIds) === null || _q === void 0 ? void 0 : _q.flatMap(splitIds)) || [],
        };
    }
}
